import { useState, useEffect, useRef, useLayoutEffect } from "react";
import CustomSearch from "./CustomSearch";
import Button from "../../Buttons/Button";
import AddOptionPopup from "../../Modal/AddOptionPopup";
import Chip from "../../Chip/Chip";

import { createPortal } from "react-dom";
import { formatWords2 } from "../../../utils/stringUtils";
import InfoCircle from "../../Info/InfoCircle";
import SmartTooltip from "../../Info/SmartTooltip";

const sizeClasses = {
  xs: "px-2 py-0",
  "xs-sm": "px-2.5 py-1",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const iconSizeClasses = {
  xs: "!text-[12px]",
  "xs-sm": "!text-[14px]",
  sm: "!text-[14px]",
  md: "!text-[14px]",
  lg: "!text-[15px]",
  xl: "!text-[15px]",
};

const CustomSelectNew = ({
  options = [],
  selected,
  onChange,
  placeholder = "Select an option",
  showSearch = false,
  size = "md",
  rounded = "rounded",
  showButton = false,
  alertName,
  icon = false,
  minwidth = "min-w-32",
  disabled,
  refData,
  handleScroll,
  keyname,
  searchValue = "",
  onSearch,
  onClose,
  multiple = false,
  style = "normal", // normal, chip
  fetchedOriginalData,
  onSelectItem,
  noBorder = false,
  shadow = false,
  showClearButton=false, // to clear the input value
  showBadges = false, // NEW: Enable badge display
  badgeSize = "xs", // NEW: Size for InfoCircle badges
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [customOptions, setCustomOptions] = useState(options || []);
  const [selectedOptions, setSelectedOptions] = useState(multiple ? [] : null);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const dropdownRef = useRef(null);
  const selectedLabelsRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const [isRightAligned, setIsRightAligned] = useState(false);
  const [isBelowPositioned, setIsBelowPositioned] = useState(true);
  const shouldPersist = rest["data-dropdown-persist"] === "true";

  // Initialize selected options from props
  useEffect(() => {
    if (selected !== undefined) {
      if (multiple) {
        const selectedArray = Array.isArray(selected) ? selected : [];
        const fullOptions = selectedArray
          .map((item) => {
            if (
              typeof item === "object" &&
              item !== null &&
              item.value !== undefined
            ) {
              return item;
            } else {
              const matchingOption = customOptions.find(
                (opt) => opt && String(opt.value) === String(item),
              );
              return matchingOption || { value: item, label: String(item) };
            }
          })
          .filter(Boolean);
        setSelectedOptions(fullOptions);
      } else {
        if (
          typeof selected === "object" &&
          selected !== null &&
          selected.value !== undefined
        ) {
          setSelectedOptions(selected);
        } else {
          const matchingOption = customOptions.find(
            (opt) => opt && String(opt.value) === String(selected),
          );
          setSelectedOptions(
            matchingOption || { value: selected, label: String(selected) },
          );
        }
      }
    } else {
      setSelectedOptions(multiple ? [] : null);
    }
  }, [selected, customOptions, multiple]);

  // Update options when props change
  useEffect(() => {
    if (options && Array.isArray(options)) {
      setCustomOptions(options);
    }
  }, [options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }

      let element = event.target;
      while (element) {
        if (
          element.getAttribute &&
          element.getAttribute("data-dropdown-persist") === "true"
        ) {
          return;
        }
        element = element.parentElement;
      }

      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      if (onClose) {
        onClose();
      }
    }
  }, [isOpen, onClose]);

  // Calculate visible labels based on container width for multiple selection
  useEffect(() => {
    if (
      multiple &&
      selectedOptions &&
      Array.isArray(selectedOptions) &&
      selectedOptions.length > 0 &&
      selectedLabelsRef.current
    ) {
      const calculateVisibleLabels = () => {
        const containerWidth = selectedLabelsRef.current.offsetWidth;
        const tempSpan = document.createElement("span");
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.fontSize = fontSizeClasses[size].replace("text-", "");
        document.body.appendChild(tempSpan);

        let totalWidth = 0;
        let visibleCount = 0;

        for (let i = 0; i < selectedOptions.length; i++) {
          if (!selectedOptions[i]) continue;

          if (style === "chip") {
            const displayText =
              selectedOptions[i].label && selectedOptions[i].label.length > 20
                ? selectedOptions[i].label.substring(0, 20) + "..."
                : selectedOptions[i].label || String(selectedOptions[i].value);

            tempSpan.textContent = displayText;
            const labelWidth = tempSpan.offsetWidth + 24;

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          } else {
            const maxItemWidth = 250;
            tempSpan.textContent =
              selectedOptions[i].label || String(selectedOptions[i].value);
            const actualWidth = tempSpan.offsetWidth;
            const labelWidth = Math.min(actualWidth, maxItemWidth) + 24;

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          }
        }

        document.body.removeChild(tempSpan);

        setVisibleLabels(selectedOptions.slice(0, visibleCount));
        setHiddenCount(selectedOptions.length - visibleCount);
      };

      calculateVisibleLabels();

      window.addEventListener("resize", calculateVisibleLabels);
      return () => window.removeEventListener("resize", calculateVisibleLabels);
    } else {
      setVisibleLabels([]);
      setHiddenCount(0);
    }
  }, [selectedOptions, size, style, multiple]);

  // Filter options based on search input
  const filteredOptions = customOptions.filter((option) => {
    if (!option || typeof option.label !== "string") return false;

    const searchTerm = search || "";
    if (searchTerm.trim() === "") {
      return true;
    }
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const AddNew = () => {
    setShowPopup(!showPopup);
  };

  const handleAddOption = (newOption) => {
    const updatedOptions = [...customOptions, newOption].sort((a, b) =>
      a.label.replace(/\s+/g, "").localeCompare(b.label.replace(/\s+/g, "")),
    );
    setCustomOptions(updatedOptions);
    handleOptionSelect(newOption);
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (multiple) {
      let updatedSelection;

      const isSelected = selectedOptions.some(
        (item) => item && String(item.value) === String(option.value),
      );

      if (isSelected) {
        updatedSelection = selectedOptions.filter(
          (item) => item && String(item.value) !== String(option.value),
        );
      } else {
        updatedSelection = [...selectedOptions, option];
      }

      setSelectedOptions(updatedSelection);

      if (onChange) {
        const values = updatedSelection.map((item) => item.value);
        onChange(values);
      }
    } else {
      setSelectedOptions(option);
      if (onChange) {
        onChange(option.value);
      }
      if (onSelectItem) {
        onSelectItem(options, fetchedOriginalData, option.value, option);
      }
      setIsOpen(false);
    }
  };

  const handleRemoveOption = (optionToRemove) => {
    if (multiple && Array.isArray(selectedOptions)) {
      const newSelectedOptions = selectedOptions.filter(
        (option) =>
          option && String(option.value) !== String(optionToRemove.value),
      );
      setSelectedOptions(newSelectedOptions);
      if (onChange) {
        const values = newSelectedOptions.map((opt) => opt.value);
        onChange(values);
      }
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        const showBelow =
          spaceBelow >= Math.min(320, dropdownRect.height) ||
          spaceBelow >= spaceAbove;

        let left = buttonRect.left;
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        let top;
        if (showBelow) {
          top = buttonRect.bottom + 8;
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 8;
          if (top < 0) {
            top = 8;
          }
        }

        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
        setIsBelowPositioned(showBelow);
      };

      updatePosition();

      const handleScroll = () => {
        requestAnimationFrame(updatePosition);
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  const renderBadges = (option) => {
    if (!showBadges || !option.badges) return null;

    return (
      <div className="flex items-center gap-1 ml-2">
        {Object.entries(option.badges).map(([key, badgeData]) => {
          const badgeValue =
            typeof badgeData === "object" ? badgeData.value : badgeData;
          const badgeColor =
            typeof badgeData === "object" && badgeData.color
              ? badgeData.color
              : "primary";
          const badgeColorDepth =
            typeof badgeData === "object" && badgeData.colorDepth
              ? badgeData.colorDepth
              : "500";

          return (
            <SmartTooltip description={key} noicon={true}>
              <InfoCircle
                key={key}
                value={badgeValue}
                size={badgeSize}
                color={badgeColor}
                colorDepth={badgeColorDepth}
                variant="contained"
                noBorder
              />
            </SmartTooltip>
          );
        })}
      </div>
    );
  };

  // Get display value for the select button
  const getDisplayValue = () => {
    if (multiple) {
      if (
        !selectedOptions ||
        !Array.isArray(selectedOptions) ||
        selectedOptions.length === 0
      ) {
        return placeholder;
      }

      return (
        <div
          ref={selectedLabelsRef}
          className="flex flex-wrap gap-1 max-w-full overflow-hidden"
        >
          {visibleLabels.map(
            (option, index) =>
              option && (
                <div key={index} className="flex items-center">
                  {style === "chip" ? (
                    <Chip
                      label={option.label || String(option.value)}
                      size="xs"
                      onDelete={() => handleRemoveOption(option)}
                    />
                  ) : (
                    <span
                      className="bg-info-50 px-2 py-0.5 rounded-md flex items-center gap-1 max-w-[150px]"
                      title={option.label || String(option.value)}
                    >
                      <span className="truncate">
                        {option.label || String(option.value)}
                      </span>
                      <span
                        className="material-icons !text-[14px] cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOption(option);
                        }}
                      >
                        close
                      </span>
                    </span>
                  )}
                </div>
              ),
          )}
          {hiddenCount > 0 && (
            <span className="text-gray-500 whitespace-nowrap">
              +{hiddenCount} more
            </span>
          )}
        </div>
      );
    } else {
      if (!selectedOptions) return placeholder;

      const displayText =
        selectedOptions.label || String(selectedOptions.value);

      return displayText || placeholder;
    }
  };

  const handleClear = () => {
    const empty = multiple ? [] : "";
    setSelectedOptions(empty);
    if (onChange) {
      onChange(multiple ? [] : "");
    }
    setIsOpen(false);
  };

  // Check if an option is selected
  const isOptionSelected = (option) => {
    if (!option) return false;

    if (multiple) {
      return (
        Array.isArray(selectedOptions) &&
        selectedOptions.some(
          (item) => item && String(item.value) === String(option.value),
        )
      );
    } else {
      return (
        selectedOptions &&
        String(selectedOptions.value) === String(option.value)
      );
    }
  };

  // NEW: Render dropdown content
  const renderDropdownContent = () => (
    <>
      {(showSearch || showButton) && (
        <div className="p-1 border-b border-gray-200">
          {showSearch && (
            <CustomSearch
              placeholder="Search..."
              value={searchValue !== "" ? searchValue : search}
              onChange={
                onSearch
                  ? (key, value) => onSearch(value)
                  : (key, value) => setSearch(value)
              }
              size={size}
              rounded="rounded-full"
              keyname="search"
            />
          )}
          {showButton && (
            <div className="flex justify-end mt-1">
              <Button title={"Add New"} onClick={AddNew} icon={icon} />
            </div>
          )}
        </div>
      )}
      <div
        className="max-h-48 overflow-y-auto"
        onScroll={handleScroll}
        ref={refData}
      >
        {filteredOptions.length > 0 ? (
          <ul>
            {filteredOptions.map((option) => (
              // <li
              //   key={option.value}
              //   className={`p-2 flex justify-between items-center cursor-pointer ${
              //     isOptionSelected(option)
              //       ? "bg-primary text-white hover:bg-primary-700"
              //       : "hover:bg-primary-50"
              //   }`}
              //   onClick={() => handleOptionSelect(option)}
              // >
              // it is changed to accept disable feature for options in dropdown
              <li
                key={option.value}
                className={`p-2 text-sm flex justify-between items-center 
    ${option.isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
    ${
      isOptionSelected(option)
        ? "bg-primary text-white hover:bg-primary-700"
        : option.isDisabled
          ? ""
          : "hover:bg-primary-50"
    }
  `}
                onClick={() => {
                  if (!option.isDisabled) {
                    handleOptionSelect(option);
                  }
                }}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <span className="truncate">{option.label}</span>
                  {renderBadges(option)}
                </div>
                {isOptionSelected(option) && (
                  <span className="material-icons !text-[16px] ml-2 flex-shrink-0">
                    check
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-2 text-gray-500 text-center">No options found</div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full border flex justify-between items-center focus:outline-none ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} 
         ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
        ${
          disabled
            ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
            : "bg-white hover:border-primary focus:border-primary cursor-pointer"
        }
        ${noBorder ? "border-0" : ""}
        `}
        onClick={toggleDropdown}
        type="button"
        disabled={disabled}
        ref={buttonRef}
        {...rest}
      >
        <div
          className={`line-clamp-1 text-left flex-1 overflow-hidden ${
            (multiple && selectedOptions && selectedOptions.length > 0) ||
            (!multiple && selectedOptions?.value)
              ? "text-black"
              : "text-neutrals-500"
          }`}
        >
          {getDisplayValue()}
        </div>
        {showClearButton &&
          ((multiple && selectedOptions?.length > 0) ||
            (!multiple && selectedOptions)) &&
          !disabled && (
            <span
              className={`material-icons ${iconSizeClasses[size]} ml-1 flex-shrink-0 hover:text-red-500`}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              close
            </span>
          )}
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </div>

      {shouldPersist
        ? isOpen && (
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {renderDropdownContent()}
            </div>
          )
        : isOpen &&
          createPortal(
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {renderDropdownContent()}
            </div>,
            document.querySelector(".select-root"),
          )}

      {/* Add Option Popup */}
      {showPopup &&
        createPortal(
          <AddOptionPopup
            onClose={() => setShowPopup(false)}
            onAdd={handleAddOption}
            existingOptions={customOptions}
            alertName={alertName}
          />,
          document.querySelector(".select-root"),
        )}
    </div>
  );
};

export default CustomSelectNew;
